import { useState, useEffect, useCallback, useMemo } from "react";

import { isNonEmptyArray, isNonEmptyObject, isNonEmpty, shallowCopy, b64toBlob } from "_helpers";
import { 
    getFolders, 
    getFolderDetail, 
    getFolderMessages, 
    getFolderMessageAttachment,
    sendFolderMessage,
    sendNextFile
} from "_services";

export function useFolders(){
    const [ gridData, setGridData ] = useState([]);
    const [ chartsData, setChartsData ] = useState({});
    const [ chartsLegend, setChartsLegend ] = useState([]);

    const [ fetchDataAreReady, setFetchDataAreReady ] = useState(false);
    const [ fetchIsDone, setFetchIsDone ] = useState(false);

    const resetData = useCallback(() => {
        setGridData([]);
        setChartsData({});
        setChartsLegend([]);
    }, []);

    useEffect(() => {
        try{
            const timer = setTimeout(async () => {
                const { response, data } = await getFolders();

                if( response ){
                    const { records, charts } = data;

                    setGridData(records);
                    setChartsData({
                        chart01: charts?.chart01 || [],
                        chart02: charts?.chart02 || [],
                    });
                    setChartsLegend(charts?.legend || []);

                    setFetchDataAreReady( (isNonEmptyArray(records) && isNonEmptyObject(charts)) );
                    setFetchIsDone(true);
                } else {
                    resetData();
                    setFetchDataAreReady(false);
                    setFetchIsDone(true);
                }
            }, 0)

            return () => clearTimeout(timer);
        } catch(e){
            throw new Error(e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return {
        fetchGridData: gridData,
        fetchChartsData: chartsData,
        fetchChartsLegend: chartsLegend,
        fetchDataAreReady,
        fetchIsDone
    }
}

export function useFolderDetail({ id }){
    const [ contactsData, setContactsData ] = useState({});
    const [ invoicesData, setInvoicesData ] = useState({});
    const [ actionsData, setActionsData ] = useState([]);

    const resetData = useCallback(() => {
        setContactsData({});
        setInvoicesData({});
        setActionsData([]);
    }, []);

    useEffect(() => {
        try{
            const timer = setTimeout(async () => {
                const { response, data } = await getFolderDetail({ id });

                if( response ){
                    setContactsData(data?.contacts || {});
                    setInvoicesData(data?.invoices || {});
                    setActionsData(data?.actions || []);
                } else {
                    resetData();
                }
            }, 0)

            return () => clearTimeout(timer);
        } catch(e){
            throw new Error(e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return { 
        fetchContactsData: contactsData,
        fetchInvoicesData: invoicesData, 
        fetchActionsData: actionsData
    };
}

export function useFolderDetailGetMessages( modalIsOpen, values ){
    const [ messages, setMessages ] = useState([]);
    const [ quiz, setQuiz ] = useState([]);
    const [ inError, setInError ] = useState(false);

    const doReload = (skipQuiz = false) => {
        values['skip_quiz'] = skipQuiz;
        try{
            const timer = setTimeout(async () => {
                const { response, data, isQuiz } = await getFolderMessages(values);
                if( response && isNonEmpty(data) ){
                    if( isQuiz ){
                        setMessages([]);
                        setQuiz(data);
                    } else {
                        setMessages(data);
                        setQuiz([])
                    } 
                } else {
                    setInError(true);
                    setMessages([]);
                    setQuiz([]);
                }
            }, 0)

            return () => clearTimeout(timer);
        } catch(e){
            setInError(true);
            setMessages([]);
            setQuiz([]);
            throw new Error(e);
        }
    }

    useEffect(() => {
        if( modalIsOpen ){
            doReload();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalIsOpen])

    return { 
        inError,
        doReload,
        fetchMessages: messages,
        fetchQuiz: quiz
    }
}

export function useFolderDetailGetMessageAttachment( ){
    const [ binId, setBinId ] = useState('');

    useEffect(() => {
        if( binId ){
            try{
                const timer = setTimeout(async () => {
                    const { response, data } = await getFolderMessageAttachment(binId);
                    
                    if( response ){
                        const { bin_64, bin_filename} = data;

                        let bin = b64toBlob(bin_64) ;
                        const url = window.URL.createObjectURL(new Blob([bin]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', bin_filename);
                        document.body.appendChild(link);
                        link.click();
                    }

                    setBinId("");
                }, 0)
    
                return () => clearTimeout(timer);
            } catch(e){
                setBinId("");
                throw new Error(e);
            }
        }
    }, [binId])

    return {
        setBinId
    }
}

export function useFolderDetailSendMessage( payload ){
    const initialValues = useMemo(() => ({
        text: "",
        attachments: []
    }), [])
    const [ values, setValues ] = useState({});
    const [ isSubmitted, setIsSubmitted ] = useState(false);

    const [ successMessage, setSuccessMessage ] = useState("");
    const [ errorMessage, setErrorMessage ] = useState("");

    const handleReset = ( ) => {
        setValues(initialValues);
        setIsSubmitted(false);
        setSuccessMessage("");
        setErrorMessage("");
    }

    useEffect(() => {
        if( isNonEmptyObject(payload) && isNonEmptyObject(values) && isSubmitted ){
            let valuesCopy = shallowCopy(values);
            const { text, attachments } = valuesCopy;

            const formData = new FormData();
            Object.entries(payload).forEach(([key, val]) => {
                formData.append(key, val);
            })
            formData.append('text', text);

            if( isNonEmptyArray(attachments) ){
                attachments.forEach(({ originFileObj })=> formData.append('attachments[]', originFileObj));
            }

            valuesCopy = formData;
            try{
                const timer = setTimeout(async () => {
                    const { response, successMsg, errorMsg } = await sendFolderMessage(valuesCopy);
                    
                    if( response ){
                        setSuccessMessage(successMsg);
                    } else {
                        setErrorMessage(errorMsg);
                    }
                }, 0)

                return () => clearTimeout(timer);
            } catch(e){
                throw new Error(e);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payload, values, isSubmitted])

    return {
        textareaValuesHandler: {
            values, 
            setValues,
            initialValues
        },
        textareaResetValues: handleReset,
        setSubmit: setIsSubmitted,
        successMessage,
        errorMessage
    }
}

export function useFolderDetailSendNextFile( payload ){
    const [ answers, setAnswers ] = useState({});

    const postNextFile = async ( ) => {
        const nextFileCode = answers?.nextFileCode ?? null;
        if( !nextFileCode || Object.values(payload).some(e => (e === null || e === undefined || e === '')) ) return ;

        const nextFileComment = answers?.nextFileComment ?? null;
        const nextFileAttachments = answers?.attachments ?? null;

        let result = Object.keys(answers)
            .filter(key => key.startsWith("question_"))
            .sort((a, b) => {
                const numA = parseInt(a.split("_")[1], 10);
                const numB = parseInt(b.split("_")[1], 10);
                return numA - numB;
            })
            .map((key, index) => {
                const q = answers[key];
                return `Question ${index + 1} : ${q.question}\nRéponse ${index + 1} : ${q.answer}`;
            })
            .join("\n\n");

        if( nextFileComment ) result += '\n\nCommentaire : ' + nextFileComment
        if( nextFileAttachments ){
            result += '\nPièce(s) jointe(s) :';
            nextFileAttachments.forEach((el, idx) => {
                result += '\n\t#' + (idx+1) + ' : ' + el.name;
            })
        }

        const formData = new FormData();
        Object.entries(payload).forEach(([key, val]) => {
            formData.append(key, val);
        })
        formData.append('next_file_code', nextFileCode);
        formData.append('next_file_message', result);

        if( isNonEmptyArray(nextFileAttachments) ){
            nextFileAttachments.forEach(({ originFileObj })=> formData.append('attachments[]', originFileObj));
        }

        try{
            const { response } = await sendNextFile(formData);
        } catch(e){
            throw new Error(e);
        }

        setAnswers({});
    }

    return {
        answers,
        setAnswers,
        postNextFile
    }
}